import React from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withFirebase } from '../Firebase';
import { withAdminAuthorization } from '../Session';
import './AdminDashboard.css';

const BUILDER_SECTIONS = [
  {
    key: 'strategicConsultant',
    label: 'Strategic Consultant',
    description: 'Project positioning, GTM, and commercialization support.',
  },
  {
    key: 'projectManagement',
    label: 'Project Management Consultant',
    description: 'Execution oversight and delivery support.',
  },
  {
    key: 'capitalMarket',
    label: 'Capital Market Transaction',
    description: 'Capital raise, debt, and structured finance.',
  },
  {
    key: 'valuation',
    label: 'Valuation',
    description: 'Independent valuation requests.',
  },
  {
    key: 'marketingAdvisory',
    label: 'Marketing Advisory',
    description: 'Positioning, creative, and channel orchestration.',
  },
  {
    key: 'mandates',
    label: 'Mandates',
    description: 'Exclusive mandates and partner programs.',
  },
];

const SUMMARY_FIELDS = {
  strategicConsultant: ['projectName', 'location', 'projectStage', 'timeline'],
  projectManagement: ['projectName', 'location', 'projectType', 'currentStage'],
  capitalMarket: ['projectName', 'location', 'funding', 'instrument'],
  marketingAdvisory: ['projectName', 'location', 'segments', 'positioning'],
  mandates: ['projectName', 'location', 'scope', 'term'],
};

const formatFieldLabel = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatTimestamp = (value) => {
  if (!value) return 'N/A';
  if (value.toDate) return value.toDate().toLocaleString();
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
  }
  return String(value);
};

class BuilderDashboardBase extends React.Component {
  state = {
    loading: true,
    currentSection: BUILDER_SECTIONS[0].key,
    requests: [],
    error: null,
  };

  componentDidMount() {
    this.unsubscribe = this.props.firebase.db
      .collection('builderRequests')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const requests = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          this.setState({ requests, loading: false, error: null });
        },
        (error) => {
          console.error('Error loading builder requests:', error);
          this.setState({
            loading: false,
            error: 'Unable to load builder requests.',
          });
        },
      );
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  handleSignOut = () => {
    this.props.firebase.doSignOut();
  };

  handleSectionChange = (key) => {
    this.setState({ currentSection: key });
  };

  getFilteredRequests = () => {
    const { requests, currentSection } = this.state;
    return requests.filter((request) => request.serviceType === currentSection);
  };

  buildSummary = (request) => {
    const fields = SUMMARY_FIELDS[request.serviceType] || [];
    const data = request.data || {};
    const summaryParts = fields
      .map((field) => {
        const value = data[field];
        return value ? `${formatFieldLabel(field)}: ${value}` : null;
      })
      .filter(Boolean);
    if (summaryParts.length > 0) {
      return summaryParts.join(' | ');
    }
    return request.serviceName || 'Request submitted';
  };

  resolveContactName = (request) => {
    const data = request.data || {};
    return (
      data.contactName ||
      data.primaryContact ||
      data.promoter ||
      data.name ||
      data.fullName ||
      'N/A'
    );
  };

  resolveContactEmail = (request) => {
    const data = request.data || {};
    return request.userEmail || data.contactEmail || data.email || 'N/A';
  };

  resolveContactPhone = (request) => {
    const data = request.data || {};
    return data.contactPhone || data.phone || data.mobile || 'N/A';
  };

  resolveProjectName = (request) => {
    const data = request.data || {};
    return data.projectName || data.projectTitle || data.developmentName || 'N/A';
  };

  render() {
    const { loading, currentSection, error } = this.state;
    const activeSection =
      BUILDER_SECTIONS.find((section) => section.key === currentSection) ||
      BUILDER_SECTIONS[0];
    const sectionRequests = this.getFilteredRequests();

    return (
      <div className="admin-dashboard">
        <header className="admin-header">
          <div className="admin-header-content">
            <div className="admin-branding">
              <h1>Builder Dashboard</h1>
              <p>Douglas & McCartney</p>
            </div>
            <button onClick={this.handleSignOut} className="admin-signout-btn">
              <span>Sign Out</span>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </header>

        <main className="admin-main">
          <div className="admin-layout">
            <aside className="admin-sidebar">
              <nav className="admin-nav">
                {BUILDER_SECTIONS.map((section) => (
                  <button
                    key={section.key}
                    className={`nav-item ${currentSection === section.key ? 'active' : ''}`}
                    onClick={() => this.handleSectionChange(section.key)}
                  >
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6M5 7a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7z" />
                    </svg>
                    <span>{section.label}</span>
                  </button>
                ))}
              </nav>
            </aside>

            <div className="admin-content-area">
              <div className="admin-panel-section">
                <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', alignItems: 'flex-start', marginBottom: '24px' }}>
                  <div>
                    <h3>{activeSection.label}</h3>
                    <p className="panel-sub">{activeSection.description}</p>
                  </div>
                  <div style={{ fontSize: '14px', color: '#6c757d' }}>
                    Total requests: {sectionRequests.length}
                  </div>
                </div>

                <div className="admin-table-card">
                  {loading ? (
                    <div className="empty-state">
                      <p>Loading builder requests...</p>
                    </div>
                  ) : error ? (
                    <div className="empty-state">
                      <p>{error}</p>
                    </div>
                  ) : sectionRequests.length === 0 ? (
                    <div className="empty-state">
                      <p>No requests yet for this section.</p>
                    </div>
                  ) : (
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Project</th>
                          <th>Submitted By</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Submitted</th>
                          <th>Summary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sectionRequests.map((request) => (
                          <tr key={request.id}>
                            <td>{this.resolveProjectName(request)}</td>
                            <td>{this.resolveContactName(request)}</td>
                            <td>{this.resolveContactEmail(request)}</td>
                            <td>{this.resolveContactPhone(request)}</td>
                            <td>{formatTimestamp(request.createdAt)}</td>
                            <td>{this.buildSummary(request)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
}

const BuilderDashboard = compose(
  withRouter,
  withFirebase,
  withAdminAuthorization,
)(BuilderDashboardBase);

export default BuilderDashboard;
